import { useState, useEffect } from "react";
import { DateRangePicker, SelectPicker, Pagination } from "rsuite";
import { UtilityDate } from "../../utils";
import { showToast } from "../../components/toast.component";
import { AppointmentCheckDataExchangeTable } from "./appointmentCheckDataExchangeTable.component";
import { ApiDataExchange } from "../../core/apis/dataExchange";
import { ApiAppointmentCheck, ApiPatient } from "../../core/apis";
import { Button } from "../../components/button.component";
import { ApiSchemaFilteredAppointmentCheck, ApiSchemaAppointmentCheck } from "../../models/api/appointmentCheck";
import { startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, subWeeks } from 'date-fns';

interface IProps {
  selectedCompany: string;
}

// Update type definitions to match rsuite exactly
type DateRange = [(Date | undefined)?, (Date | undefined)?];
type ValueType = DateRange;

const GAP_DAYS = 90; // Updated to 90 days (3 months)

export function AppointmentDataExchange({ selectedCompany }: IProps) {
  // Update state types
  const [appointmentDateRange, setAppointmentDateRange] = useState<ValueType>([undefined, undefined]);
  const [visitDateRange, setVisitDateRange] = useState<ValueType>([undefined, undefined]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [apiData, setApiData] = useState<ApiSchemaFilteredAppointmentCheck | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<string>("");
  const [insuranceId, setInsuranceId] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(30); // Changed from 10 to 30
  const { data: patients } = ApiPatient.GetAllFromCompany.Hook(selectedCompany);
  const { data: insuranceTypes } = ApiPatient.GetAllInsuranceTypes.Hook(selectedCompany);

  // Derived states for compatibility with existing code
  const startDate = appointmentDateRange[0] || null;
  const endDate = appointmentDateRange[1] || null; 
  const visitStartDate = visitDateRange[0] || null;
  const visitEndDate = visitDateRange[1] || null;

  // Reset selected patient and insurance when company changes
  useEffect(() => {
    setSelectedPatient("");
    setInsuranceId("");
    setApiData(null);
    setPageNumber(1);
    setAppointmentDateRange([undefined, undefined]);
    setVisitDateRange([undefined, undefined]);
  }, [selectedCompany]);

  // Reset page number when filters change
  useEffect(() => {
    setPageNumber(1);
  }, [selectedPatient, insuranceId]);

  // Reset page number when date ranges change
  useEffect(() => {
    setPageNumber(1);
  }, [appointmentDateRange, visitDateRange]);

  const filteredPatients = patients?.filter(patient => 
    insuranceId ? patient.insuranceTypeId === insuranceId : true  // Show all patients when no insurance selected
  );

  const hasValidGap = (() => {
    if (!startDate || !endDate) {
      return false;
    }
    return UtilityDate.isValidGap(startDate, endDate, GAP_DAYS);
  })();

  const hasValidVisitGap = (() => {
    if (!visitStartDate || !visitEndDate) {
      return true; // Visit date range is optional
    }
    return UtilityDate.isValidGap(visitStartDate, visitEndDate, GAP_DAYS);
  })();

  const hasNotSynced = apiData?.list?.some((item: ApiSchemaAppointmentCheck) => item.isDESynced === false);

  // Update handler functions
  const handleAppointmentDateRangeChange = (value: ValueType) => {
    if (!value || !Array.isArray(value)) {
      setAppointmentDateRange([undefined, undefined]);
      return;
    }
    
    const [start, end] = value;
    
    if (start && end) {
      const maxEndDate = new Date(start.getTime());
      maxEndDate.setDate(maxEndDate.getDate() + GAP_DAYS);
      
      if (end > maxEndDate) {
        setAppointmentDateRange([start, maxEndDate]);
        return;
      }
    }
    
    setAppointmentDateRange(value);
  };

  const handleVisitDateRangeChange = (value: ValueType) => {
    if (!value || !Array.isArray(value)) {
      setVisitDateRange([undefined, undefined]);
      return;
    }
    
    const [start, end] = value;
    
    if (start && end) {
      const maxEndDate = new Date(start.getTime());
      maxEndDate.setDate(maxEndDate.getDate() + GAP_DAYS);
      
      if (end > maxEndDate) {
        setVisitDateRange([start, maxEndDate]);
        return;
      }
    }
    
    setVisitDateRange(value);
  };

  const getAppointmentCheckData = () => {
    if (!hasValidGap) {
      showToast(
        `Start and End Date must be within a ${GAP_DAYS} day range`,
        "info",
        "topEnd",
        15000,
        true
      );
      return;
    }

    if (!hasValidVisitGap) {
      showToast(
        `Visit Start and End Date must be within a ${GAP_DAYS} day range`,
        "info",
        "topEnd",
        15000,
        true
      );
      return;
    }

    // Check if necessary dates exist before proceeding
    if (!startDate || !endDate) {
      showToast(
        "Please select appointment date range",
        "info",
        "topEnd",
        5000,
        true
      );
      return;
    }

    // Ensure dates exist - safe to call toISOString
    const isoStartDate = startDate.toISOString();
    const isoEndDate = endDate.toISOString();
    const isoVisitStartDate = visitStartDate?.toISOString();
    const isoVisitEndDate = visitEndDate?.toISOString();

    // Reset the table data before making the API call
    setApiData(null);

    const data = {
      pageNumber, // updated pagination
      pageSize,   // updated pagination
      nextPageToken: "",
      startDate: isoStartDate,
      endDate: isoEndDate,
      visitStartDate: isoVisitStartDate,
      visitEndDate: isoVisitEndDate,
      companyId: selectedCompany,
      patientId: selectedPatient || undefined,
      insuranceId: insuranceId || undefined   // <-- insuranceId added
    };
    ApiAppointmentCheck.GetByDateRange({
      data,
      onStart: () => setIsFetching(true),
      onEnd: () => setIsFetching(false),
      onSuccess: (response: any) => setApiData(response),
    });
  };

  const handleSync = async () => {
    // Check if necessary dates exist before proceeding
    if (!startDate || !endDate) {
      showToast(
        "Please select appointment date range",
        "info",
        "topEnd",
        5000,
        true
      );
      return;
    }

    // Ensure dates exist - safe to call toISOString
    const isoStartDate = startDate.toISOString();
    const isoEndDate = endDate.toISOString();
    const isoVisitStartDate = visitStartDate?.toISOString();
    const isoVisitEndDate = visitEndDate?.toISOString();
    
    const data = {
      pageNumber: 0,
      pageSize: 30,
      nextPageToken: "",
      startDate: isoStartDate,
      endDate: isoEndDate,
      visitStartDate: isoVisitStartDate,
      visitEndDate: isoVisitEndDate,
      companyId: selectedCompany,
      patientId: selectedPatient || undefined,
      insuranceId: insuranceId || undefined 
    };
    await ApiDataExchange.SyncAppointment({
      data,
      onStart: () => setIsSyncing(true),
      onSuccess: () => {
        // Reset the table data before making the API call
        setApiData(null);
        ApiAppointmentCheck.GetByDateRange({
          data,
          onStart: () => setIsFetching(true),
          onEnd: () => setIsFetching(false),
          onSuccess: (response: any) => setApiData(response),
        })
      },
      onEnd: () => setIsSyncing(false),
    });
  };

  // Update clear handler
  const handleClear = () => {
    setAppointmentDateRange([undefined, undefined]);
    setVisitDateRange([undefined, undefined]);
    setApiData(null);
    setSelectedPatient("");
    setInsuranceId("");
    setPageNumber(1);
  };

  const handlePageChange = (page: number) => {
    setPageNumber(page);
    getAppointmentCheckData();
  };

  // Date preset functions
  const setThisMonth = () => {
    const now = new Date();
    setAppointmentDateRange([startOfMonth(now), endOfMonth(now)]);
  };

  const setLastMonth = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    setAppointmentDateRange([startOfMonth(lastMonth), endOfMonth(lastMonth)]);
  };

  const setLastTwoMonths = () => {
    const now = new Date();
    const twoMonthsAgo = subMonths(now, 2);
    setAppointmentDateRange([startOfMonth(twoMonthsAgo), endOfMonth(now)]);
  };

  const setLastWeek = () => {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    setAppointmentDateRange([
      startOfWeek(lastWeek, { weekStartsOn: 1 }),
      endOfWeek(lastWeek, { weekStartsOn: 1 })
    ]);
  };

  const setVisitThisMonth = () => {
    const now = new Date();
    setVisitDateRange([startOfMonth(now), endOfMonth(now)]);
  };

  const setVisitLastMonth = () => {
    const now = new Date();
    const lastMonth = subMonths(now, 1);
    setVisitDateRange([startOfMonth(lastMonth), endOfMonth(lastMonth)]);
  };

  const setVisitLastTwoMonths = () => {
    const now = new Date();
    const twoMonthsAgo = subMonths(now, 2);
    setVisitDateRange([startOfMonth(twoMonthsAgo), endOfMonth(now)]);
  };

  const setVisitLastWeek = () => {
    const now = new Date();
    const lastWeek = subWeeks(now, 1);
    setVisitDateRange([
      startOfWeek(lastWeek, { weekStartsOn: 1 }),
      endOfWeek(lastWeek, { weekStartsOn: 1 })
    ]);
  };

  const patientsSelectItems = filteredPatients?.map(patient => ({
    label: `${patient.firstName} ${patient.lastName}`,
    value: patient.sKey
  })) || [];

  const insuranceTypeMap = insuranceTypes?.map((it) => ({
    label: it.displayName ?? it.name,
    value: it.sKey,
  })) ?? [];

  const disabledInsuranceType = () => {
    const disabledIT: string[] = [];
    insuranceTypes?.forEach((it, idx) => {
      if (it.isDisabled) disabledIT.push(insuranceTypeMap[idx].value!);
    });
    return disabledIT;
  };

  return (
    <div className="py-1">
      <div className="row g-0 mb-4">
        {/* Filters Group */}
        <div className="col-12 mb-3">
          <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Filters:</span>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>Insurance Type</span>
              <div style={{ minWidth: "200px" }}>
                <SelectPicker
                  value={insuranceId}
                  onChange={(value) => {
                    setInsuranceId(value as string);
                    setSelectedPatient("");
                  }}
                  data={insuranceTypeMap}
                  disabledItemValues={disabledInsuranceType()}
                  placeholder="Select Insurance Type"
                  cleanable
                  block
                />
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span>Patient</span>
              <div style={{ minWidth: "200px" }}>
                <SelectPicker
                  data={patientsSelectItems}
                  value={selectedPatient}
                  onChange={value => setSelectedPatient(value as string)}
                  searchable
                  cleanable
                  disabled={!insuranceId}
                  placeholder={insuranceId ? "Select Patient" : "Select Insurance Type First"}
                  block
                />
              </div>
            </div>
          </div>
        </div>

        {/* Main Date Range Group */}
        <div className="col-12 mb-3">
          <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Appointment Date Range:</span>
            </div>
            <div className="d-flex align-items-center gap-2" style={{ minWidth: "300px" }}>
              <DateRangePicker
                value={appointmentDateRange}
                onChange={handleAppointmentDateRangeChange}
                format="MM-dd-yyyy"
                placeholder="Select Date Range"
                style={{ width: "100%" }}
                character=" - "
                disabledDate={(date) => {
                  if (!appointmentDateRange[0]) return false;
                  const maxDate = new Date(appointmentDateRange[0]);
                  maxDate.setDate(maxDate.getDate() + GAP_DAYS);
                  return date > maxDate;
                }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button name="This Month" className="btn btn-outline-primary btn-sm" onClick={setThisMonth}>This Month</Button>
              <Button name="Last Month" className="btn btn-outline-primary btn-sm" onClick={setLastMonth}>Last Month</Button>
              <Button name="Last 2 Months" className="btn btn-outline-primary btn-sm" onClick={setLastTwoMonths}>Last 2 Months</Button>
              <Button name="Last Week" className="btn btn-outline-primary btn-sm" onClick={setLastWeek}>Last Week</Button>
            </div>
          </div>
        </div>

        {/* Visit Date Range Group */}
        <div className="col-12">
          <div className="d-flex flex-wrap gap-3 p-3 border rounded bg-light align-items-center">
            <div className="d-flex align-items-center gap-2">
              <span className="fw-bold">Visit Date Range:</span>
            </div>
            <div className="d-flex align-items-center gap-2" style={{ minWidth: "300px" }}>
              <DateRangePicker
                value={visitDateRange}
                onChange={handleVisitDateRangeChange}
                format="MM-dd-yyyy"
                placeholder="Select Date Range"
                style={{ width: "100%" }}
                character=" - "
                cleanable
                disabledDate={(date) => {
                  if (!visitDateRange[0]) return false;
                  const maxDate = new Date(visitDateRange[0]);
                  maxDate.setDate(maxDate.getDate() + GAP_DAYS);
                  return date > maxDate;
                }}
              />
            </div>
            <div className="d-flex align-items-center gap-2">
              <Button name="This Month" className="btn btn-outline-primary btn-sm" onClick={setVisitThisMonth}>This Month</Button>
              <Button name="Last Month" className="btn btn-outline-primary btn-sm" onClick={setVisitLastMonth}>Last Month</Button>
              <Button name="Last 2 Months" className="btn btn-outline-primary btn-sm" onClick={setVisitLastTwoMonths}>Last 2 Months</Button>
              <Button name="Last Week" className="btn btn-outline-primary btn-sm" onClick={setVisitLastWeek}>Last Week</Button>
            </div>
            <div className="d-flex gap-2 ms-auto">
              <Button
                name="Clear"
                className="btn btn-secondary btn-sm"
                onClick={handleClear}
                disabled={!startDate && !endDate && !selectedPatient && !visitStartDate && !visitEndDate && !insuranceId}
              />
              <Button
                name="Apply Filters"
                className="btn btn-green btn-sm"
                disabled={!startDate || !endDate}
                onClick={getAppointmentCheckData}
                loading={isFetching}
              />
              {apiData && hasNotSynced && (
                <Button
                  name="Sync All"
                  className="btn btn-primary btn-sm"
                  onClick={handleSync}
                  loading={isSyncing}
                  disabled={isFetching}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {apiData && (
        <>
          <AppointmentCheckDataExchangeTable
            key={`${selectedCompany}-${pageNumber}-${insuranceId}-${selectedPatient}`}
            apiData={apiData.list}
            isFetching={isFetching}
          />
          <div className="mt-2 d-flex justify-content-between align-items-center">
            <span>Total Appointments: {apiData.totalCount}</span>
            <Pagination
              total={apiData.totalCount}
              pages={Math.ceil(apiData.totalCount / pageSize)}
              activePage={pageNumber}
              onSelect={handlePageChange}
              maxButtons={6}
              next
              prev
            />
          </div>
        </>
      )}
    </div>
  );
}
